import type { ResolvedConfig } from "../config.js";
import { CliApiClient, type TokenResponse } from "../api/cli-api.js";
import {
  credentialsFromTokenResponse,
  saveCredentials,
  type StoredCredentials,
} from "./credentials.js";
import { AspError } from "../utils/errors.js";

export function isSessionInvalidError(err: unknown): boolean {
  if (!(err instanceof AspError)) return false;
  return err.code === "ASP.AUTH.SESSION_INVALID" || err.code === "SESSION_INVALID";
}

export async function refreshStoredSession(
  config: ResolvedConfig,
  refreshToken: string,
  platformUrl: string,
): Promise<StoredCredentials> {
  const client = new CliApiClient({
    ...config,
    token: undefined,
    platformUrl: platformUrl.replace(/\/$/, ""),
  });
  const refreshed = await client.refresh(refreshToken);
  const creds = credentialsFromTokenResponse(platformUrl, refreshed);
  await saveCredentials(creds);
  return creds;
}

/** Login 返回的 access_token 需先 refresh 才能访问 /me */
export async function finalizeLogin(
  config: ResolvedConfig,
  loginResponse: TokenResponse,
): Promise<StoredCredentials> {
  if (!loginResponse.refresh_token) {
    const creds = credentialsFromTokenResponse(config.platformUrl, loginResponse);
    await saveCredentials(creds);
    return creds;
  }

  try {
    return await refreshStoredSession(
      config,
      loginResponse.refresh_token,
      config.platformUrl,
    );
  } catch {
    const creds = credentialsFromTokenResponse(config.platformUrl, loginResponse);
    await saveCredentials(creds);
    return creds;
  }
}

export async function ensureValidSession(config: ResolvedConfig): Promise<StoredCredentials> {
  const { loadCredentials, isTokenExpired } = await import("./credentials.js");
  const creds = await loadCredentials();
  if (!creds) {
    throw new AspError("Not logged in.", {
      code: "UNAUTHORIZED",
      hint: "Run `asp auth login`.",
      exitCode: 3,
    });
  }

  if (!isTokenExpired(creds) && creds.access_token) {
    return creds;
  }

  if (!creds.refresh_token) {
    throw new AspError("Session expired.", {
      code: "ASP.AUTH.SESSION_INVALID",
      hint: "Run `asp auth login`.",
      exitCode: 3,
    });
  }

  return refreshStoredSession(config, creds.refresh_token, creds.platform_url);
}
